// Single source of truth for the document preview-vs-fallback decision, shared
// by every preview surface (staff DMS, deal/collection staging, portal
// materials). Office files (.docx/.doc/.xlsx/.xls/.pptx/.ppt) are converted to
// PDF on the server and rendered inline; this helper classifies a file so each
// surface renders the iframe / image / fallback consistently.

export const OFFICE_PREVIEW_EXTENSIONS = [
  "docx",
  "doc",
  "xlsx",
  "xls",
  "pptx",
  "ppt",
] as const;

// Fragments that appear in the Office MIME types
// (e.g. "...officedocument.spreadsheetml.sheet", "application/msword").
const OFFICE_MIME_FRAGMENTS = ["officedocument", "msword", "ms-excel", "ms-powerpoint"];

export type PreviewKind = "pdf" | "image" | "text" | "office" | "other";

export interface PreviewKindInput {
  contentType?: string | null;
  fileName?: string | null;
  /**
   * Authoritative signal from the backend preview endpoint: when true the
   * resolved preview URL points at a PDF the browser can render inline — a
   * native PDF, or an Office file the server has converted. Overrides the
   * content-type / extension heuristics below.
   */
  isPdf?: boolean | null;
}

export function isOfficeExtension(ext: string): boolean {
  const normalized = ext.replace(/^\./, "").toLowerCase();
  return (OFFICE_PREVIEW_EXTENSIONS as readonly string[]).includes(normalized);
}

export function resolvePreviewKind({ contentType, fileName, isPdf }: PreviewKindInput): PreviewKind {
  if (isPdf) return "pdf";
  const type = (contentType ?? "").split(";", 1)[0]?.trim().toLowerCase() ?? "";
  const ext = (fileName ?? "").split(".").pop()?.toLowerCase() ?? "";
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("text/")) return "text";
  if (isOfficeExtension(ext) || OFFICE_MIME_FRAGMENTS.some((f) => type.includes(f))) return "office";
  return "other";
}
